'use client';

import { useEffect, useRef, useCallback } from 'react';

interface UseInViewportAnimationOptions {
  threshold?: number;
  delay?: number;
  once?: boolean; // 是否只触发一次
}

/**
 * React版本的视口动画hook
 * 基于Vue项目中的isInViewportPro函数实现
 * @param callback 进入视口时的回调函数
 * @param options 配置选项
 * @returns ref 需要绑定到监听的元素上
 */
export function useInViewportAnimation(
  callback: (target: Element) => void,
  options: UseInViewportAnimationOptions = {},
) {
  const { threshold = 0.4, delay = 100, once = true } = options;
  const elementRef = useRef<HTMLElement>(null);
  const timerRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const observerRef = useRef<IntersectionObserver | undefined>(undefined);
  const hasTriggeredRef = useRef(false);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // 如果设置了once且已经触发过，则不再触发
          if (once && hasTriggeredRef.current) {
            return;
          }

          timerRef.current = setTimeout(() => {
            callback(entry.target);
            hasTriggeredRef.current = true;

            // 如果设置了只触发一次，则取消观察
            if (once && observerRef.current) {
              observerRef.current.unobserve(entry.target);
            }
          }, delay);
        } else if (timerRef.current) {
          // 如果元素离开视口，清除定时器
          clearTimeout(timerRef.current);
        }
      });
    },
    [callback, delay, once],
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // 创建Intersection Observer
    observerRef.current = new IntersectionObserver(handleIntersection, {
      threshold,
      rootMargin: '0px',
    });

    // 延迟开始观察，确保DOM已渲染完成
    const startObserving = setTimeout(() => {
      if (element && observerRef.current) {
        observerRef.current.observe(element);
      }
    }, 100);

    return () => {
      clearTimeout(startObserving);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, threshold]);

  return elementRef;
}

/**
 * 批量添加动画类的工具函数
 * @param classNames 要查找的类名数组
 * @param animationClass 要添加的动画类名
 * @param delay 延迟时间（毫秒）
 */
export function addAnimationClasses(classNames: string[], animationClass: string, delay = 0) {
  setTimeout(() => {
    classNames.forEach((className) => {
      const elements = document.getElementsByClassName(className);
      Array.from(elements).forEach((el) => {
        el.classList.add(animationClass);
      });
    });
  }, delay);
}

/**
 * 批量添加带序列延迟的动画类
 * @param className 要查找的类名
 * @param animationClass 要添加的动画类名
 * @param delayIncrement 每个元素的延迟增量（毫秒）
 */
export function addSequentialAnimations(
  className: string,
  animationClass: string,
  delayIncrement = 100,
) {
  const elements = document.getElementsByClassName(className);
  Array.from(elements).forEach((el, index) => {
    setTimeout(
      () => {
        el.classList.add(animationClass);
      },
      delayIncrement * (index + 1),
    );
  });
}

/**
 * 移除所有动画类的清理函数
 * @param animationClasses 要移除的动画类名数组
 */
export function removeAnimationClasses(animationClasses: string[]) {
  try {
    animationClasses.forEach((className) => {
      const elements = document.querySelectorAll(`.${className}`);
      elements.forEach((element) => {
        element.classList.remove(className);
        if (element.classList.contains('opacity0')) {
          element.classList.remove('opacity0');
        }
        // 重置transform
        const htmlElement = element as HTMLElement;
        htmlElement.style.transform = 'translateY(0)';
      });
    });
  } catch {
    // 不做处理
  }
}

/**
 * 异常处理的动画清理函数
 * 当动画出现问题时，确保元素仍然可见
 */
export function handleAnimationError() {
  try {
    // 移除所有透明度为0的元素的隐藏状态
    const elements = document.querySelectorAll('.opacity0');
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.opacity = '1';
    });

    // 重置所有可能有动画变换的元素
    const animatedElements = document.querySelectorAll('.hasAnimate');
    animatedElements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      htmlElement.style.transform = 'translateY(0)';
    });
  } catch {
    // 不做处理
  }
}
